var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import { calculateProjectionMatrix, Constants, projectToWorld } from './utils';
const earthRadius = 6371008.8;
const earthCircumfrence = 2 * Math.PI * earthRadius;
function circumferenceAtLatitude(latitude) {
    return earthCircumfrence * Math.cos((latitude * Math.PI) / 180);
}
function mercatorXfromLng(lng) {
    return (180 + lng) / 360;
}
function mercatorYfromLat(lat) {
    return (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360;
}
function mercatorZfromAltitude(altitude, lat) {
    return altitude / circumferenceAtLatitude(lat);
}
function MercfromLngLat(lngLat, altitude = 0) {
    return {
        x: mercatorXfromLng(lngLat[0]),
        y: mercatorYfromLat(lngLat[1]),
        z: mercatorZfromAltitude(altitude, lngLat[1])
    };
}
function positiveDegree(degree) {
    const newDegree = degree < 0 ? 360 + degree : degree;
    return newDegree % 360;
}
export default class ThreeJSTextLayer {
    constructor(config) {
        this.initialRender = true;
        this.renderingMode = '3d';
        this.id = `floor-${config.floor.index}-3dlabels`;
        this.camera = new THREE.Camera();
        this.scene = new THREE.Scene();
        this.font = config.font;
        this.scale = config.scale;
        this.floor = config.floor;
        this.material = config.material;
        this.buildingHeight = config.buildingHeight + 0.6;
        this.mapCenter = config.mapCenter;
        this.textMeshes = [];
        this.type = 'custom';
        this.isIOS = /^(iPad|iPhone|iPod)/.test(window.navigator.platform) ||
            (/^Mac/.test(window.navigator.platform) && window.navigator.maxTouchPoints > 1);
        this.floor.clickable.forEach((feature) => {
            if (feature.properties.name && feature.properties.name.length > 0) {
                this.generateTextMesh(feature);
            }
        });
        this.floor.clickableBackground.forEach((feature) => {
            if (feature.properties.name && feature.properties.name.length > 0) {
                this.generateTextMesh(feature);
            }
        });
        const translated = MercfromLngLat(this.mapCenter, 0);
        this.modelTransform = {
            translateX: translated.x,
            translateY: translated.y,
            translateZ: translated.z,
            rotateX: 0,
            rotateY: 0,
            rotateZ: 0,
            scale: 1 / Constants.WORLD_SIZE
        };
        const light = new THREE.AmbientLight(0xffffff);
        light.intensity = 1.7;
        this.scene.add(light);
    }
    generateTextMesh(feature) {
        return __awaiter(this, void 0, void 0, function* () {
            const buildingHeight = Number(feature.properties.fillExtrusionHeight) + 0.1;
            let mesh;
            if (feature.properties.image) {
                try {
                    mesh = yield this.generateImageGeometry(feature);
                }
                catch (_a) {
                    const geo = this.generateTextGeometry(feature.properties.name || '', feature.properties.titleSize || 1);
                    mesh = new THREE.Mesh(geo, this.material);
                }
            }
            else {
                const geo = this.generateTextGeometry(feature.properties.name || '', feature.properties.titleSize || 1);
                mesh = new THREE.Mesh(geo, this.material);
            }
            const titleLocation = feature.properties.titleLocation || [0, 0];
            const textCoords = projectToWorld(titleLocation.concat([buildingHeight]));
            const centerCoords = projectToWorld(this.mapCenter);
            mesh.position.x = centerCoords.x - textCoords.x;
            mesh.position.y = centerCoords.y - textCoords.y;
            mesh.position.z = textCoords.z;
            if (feature.properties.titleRotation !== undefined && feature.properties.titleRotation !== null) {
                mesh.rotation.z = THREE.MathUtils.degToRad(feature.properties.titleRotation || 0);
            }
            const defaultRotation = THREE.MathUtils.radToDeg(mesh.rotation.z);
            const min = positiveDegree(defaultRotation - 90);
            const max = positiveDegree(defaultRotation + 90);
            mesh.frustumCulled = false;
            const newMesh = {
                name: feature.properties.name || '',
                mesh,
                originalRotation: mesh.rotation.z,
                flippedRotation: mesh.rotation.z - Math.PI,
                currentRotation: 'originalRotation',
                rotateStart: min,
                rotateEnd: max,
            };
            this.textMeshes.push(newMesh);
            this.scene.add(newMesh.mesh);
        });
    }
    generateTextGeometry(text, size) {
        const shapes = this.font.generateShapes(text.toUpperCase(), size, 0);
        const textGeo = new THREE.ShapeGeometry(shapes);
        textGeo.computeBoundingBox();
        textGeo.computeVertexNormals();
        textGeo.center();
        textGeo.computeBoundingBox();
        textGeo.scale(this.scale, this.scale, this.scale);
        return textGeo;
    }
    generateImageGeometry(feature) {
        return new Promise((resolve, reject) => {
            if (!feature.properties.image) {
                reject();
            }
            const loader = new THREE.ImageBitmapLoader();
            loader.load(feature.properties.image, (imageBitmap) => {
                let texture;
                if (this.isIOS) {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = imageBitmap.width / 2;
                    canvas.height = imageBitmap.height / 2;
                    ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);
                    const reducedImageBitmap = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    texture = new THREE.CanvasTexture(reducedImageBitmap);
                }
                else {
                    texture = new THREE.CanvasTexture(imageBitmap);
                }
                if (this.isIOS) {
                    texture.anisotropy = 0;
                    texture.magFilter = THREE.NearestFilter;
                    texture.minFilter = THREE.NearestFilter;
                }
                const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
                material.transparent = true;
                material.alphaTest = .5;
                material.needsUpdate = true;
                let plane;
                if (this.isIOS) {
                    plane = new THREE.PlaneGeometry(feature.properties.imageWidth || 100, feature.properties.imageHeight || 100, 1, 1);
                }
                else {
                    plane = new THREE.PlaneGeometry(feature.properties.imageWidth || 100, feature.properties.imageHeight || 100);
                }
                plane.computeBoundingBox();
                plane.computeVertexNormals();
                plane.center();
                plane.computeBoundingBox();
                const scale = feature.properties.imageScale || this.scale;
                plane.scale(scale, scale, scale);
                resolve(new THREE.Mesh(plane, material));
            }, undefined, () => {
                reject();
            });
        });
    }
    onAdd(map, gl) {
        this.renderer = new THREE.WebGLRenderer({
            canvas: map.getCanvas(),
            context: gl,
            antialias: true
        });
        this.renderer.autoClear = false;
        this.map = map;
    }
    render(gl, matrix) {
        if (window.currentFloorId != this.floor.floor.id || !window.mapShown) {
            return;
        }
        if (this.map) {
            const absRotation = Math.floor(this.map.getBearing()) * -1;
            const posRotation = positiveDegree(absRotation);
            this.textMeshes.forEach((textMesh) => {
                const min = textMesh.rotateStart;
                const max = textMesh.rotateEnd;
                let useRotation = 'originalRotation';
                if (max > min && (posRotation > max || posRotation < min)) {
                    useRotation = 'flippedRotation';
                }
                if (min > max && posRotation > max && posRotation < min) {
                    useRotation = 'flippedRotation';
                }
                if (textMesh.currentRotation !== useRotation) {
                    textMesh.mesh.rotation.z = textMesh[useRotation];
                    textMesh.currentRotation = useRotation;
                }
            });
        }
        this.camera.projectionMatrix.elements = matrix;
        this.camera.projectionMatrix = calculateProjectionMatrix(matrix, this.modelTransform);
        if (this.renderer) {
            this.renderer.state.reset();
            this.renderer.render(this.scene, this.camera);
        }
        if (this.initialRender && this.map) {
            this.map.triggerRepaint();
        }
        this.initialRender = false;
    }
}
//# sourceMappingURL=ThreeJSTextLayer.js.map